import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import HeaderSortSpan from "./HeaderSortSpan";
import {
  findInventoryCatalogItem,
  getCatalogSourceUrl,
  getLocalizedInventoryItemName,
} from "../../catalogs/inventoryCatalog";
import { systemLabelKey } from "../../catalogs/characterSystemCatalog";

const InventoryTable = ({ character, searchTerm, setFormData, setIsModalOpen, onDeleteItem }) => {
  const { i18n, t } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language) === "en" ? "en" : "ru";
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const inventory = useMemo(
    () => character.inventory.items || [],
    [character.inventory.items]
  );

  const localizedInventory = useMemo(
    () => inventory.map((item) => {
      const catalogItem = item.catalogId
        ? findInventoryCatalogItem(item.catalogId)
        : undefined;

      return {
        item,
        catalogItem,
        displayName: getLocalizedInventoryItemName(item, language),
        displayType: t(systemLabelKey("itemTypes", item.type), {
          defaultValue: item.type,
        }),
      };
    }),
    [inventory, language, t]
  );

  const sortedAndFilteredInventory = useMemo(() => {
    const normalizedSearchTerm = searchTerm.toLowerCase();
    let sortableItems = localizedInventory.filter(
      ({ item, displayName, displayType }) =>
        displayName.toLowerCase().includes(normalizedSearchTerm) ||
        displayType.toLowerCase().includes(normalizedSearchTerm) ||
        item.name.toLowerCase().includes(normalizedSearchTerm) ||
        item.type.toLowerCase().includes(normalizedSearchTerm) ||
        (item.weight &&
          item.weight
            .toString()
            .toLowerCase()
            .includes(normalizedSearchTerm)) ||
        (item.price &&
          item.price
            .toString()
            .toLowerCase()
            .includes(normalizedSearchTerm)) ||
        String(item.notes ?? "").toLowerCase().includes(normalizedSearchTerm)
    );

    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const getSortValue = (entry) => {
          if (sortConfig.key === "name") return entry.displayName;
          if (sortConfig.key === "type") return entry.displayType;
          return entry.item[sortConfig.key];
        };
        const firstValue = getSortValue(a);
        const secondValue = getSortValue(b);
        if (firstValue < secondValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (firstValue > secondValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [localizedInventory, searchTerm, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  if (sortedAndFilteredInventory.length === 0) {
    return (
      <div className="inventory-container inventory-empty-state">
        {inventory.length === 0
          ? t("inventory.empty")
          : t("inventory.noMatch")}
      </div>
    );
  }

  const handleItemClick = (item, displayName = item.name) => {
    setFormData({ ...item, name: displayName });
    setIsModalOpen(true);
  };

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <HeaderSortSpan className="item-name" keyName="name" title={t("inventory.itemName")} onSortChange={requestSort} sortConfig={sortConfig}/>
        <HeaderSortSpan className="item-castTime" keyName="type" title={t("inventory.itemType")} onSortChange={requestSort} sortConfig={sortConfig}/>
        <HeaderSortSpan className="item-quantity" keyName="amount" title={t("inventory.quantity")} onSortChange={requestSort} sortConfig={sortConfig}/>
        <HeaderSortSpan className="item-range" keyName="weight" title={t("inventory.weight")} onSortChange={requestSort} sortConfig={sortConfig}/>
        <HeaderSortSpan className="item-range" keyName="price" title={t("inventory.cost")} onSortChange={requestSort} sortConfig={sortConfig}/>
        <HeaderSortSpan className="item-notes" keyName="notes" title={t("inventory.notes")} onSortChange={requestSort} sortConfig={sortConfig}/>
        <span aria-label={t("common.actions")} />
      </div>
      {sortedAndFilteredInventory.map(({ item, catalogItem, displayName, displayType }, index) => {
        const sourceUrl = catalogItem
          ? getCatalogSourceUrl(catalogItem, language)
          : item.sourceUrl;

        return (
        <div
          aria-label={t("inventory.editItem", { name: displayName })}
          className="item"
          key={item.id || index}
          role="button"
          tabIndex="0"
          onClick={() => handleItemClick(item, displayName)}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleItemClick(item, displayName);
            }
          }}
        >
          <span className="item-name">
            {sourceUrl ? (
              <a
                aria-label={t("inventory.openSource", { name: displayName })}
                className="inventory-item-source-link"
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                {displayName}
              </a>
            ) : displayName}
          </span>
          <span className="item-castTime">{displayType}</span>
          <span className="item-quantity">{item.amount}</span>
          <span className="item-range">{item.weight}</span>
          <span className="item-range">{item.price}</span>
          <span className="item-notes">{item.notes}</span>
          <button
            aria-label={t("inventory.deleteItem", { name: displayName })}
            className="inventory-delete-button"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onDeleteItem(item.id);
            }}
          >
            &times;
          </button>
        </div>
        );
      })}
    </div>
  );
};

export default InventoryTable;
